import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../util";

export const Dialog = DialogPrimitive.Root;
export const DialogDisparador = DialogPrimitive.Trigger;
export const DialogCerrar = DialogPrimitive.Close;

function Velo({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-50 bg-tinta/40 data-[state=open]:animate-in data-[state=closed]:animate-out",
        className,
      )}
      {...props}
    />
  );
}

export const DialogContenido = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <Velo />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border border-linea-fuerte bg-fondo p-6 shadow-lg",
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 opacity-60 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
        <X className="size-4" />
        <span className="sr-only">Cerrar</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DialogContenido.displayName = "DialogContenido";

export function DialogEncabezado({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col space-y-1.5", className)} {...props} />;
}
export function DialogPie({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    />
  );
}
export const DialogTitulo = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("font-titulo text-xl font-bold", className)}
    {...props}
  />
));
DialogTitulo.displayName = "DialogTitulo";
export const DialogDescripcion = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("font-ui text-sm text-tinta-tenue", className)}
    {...props}
  />
));
DialogDescripcion.displayName = "DialogDescripcion";

/* Sheet: el mismo Dialog anclado a un lado. */
export const Sheet = DialogPrimitive.Root;
export const SheetDisparador = DialogPrimitive.Trigger;
export const SheetCerrar = DialogPrimitive.Close;

const lados = {
  derecha: "inset-y-0 right-0 h-full w-full max-w-md border-l",
  izquierda: "inset-y-0 left-0 h-full w-full max-w-md border-r",
  abajo: "inset-x-0 bottom-0 max-h-[85vh] border-t",
} as const;

export const SheetContenido = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { lado?: keyof typeof lados }
>(({ className, children, lado = "derecha", ...props }, ref) => (
  <DialogPrimitive.Portal>
    <Velo />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed z-50 flex flex-col gap-4 overflow-y-auto border-linea-fuerte bg-fondo p-6 shadow-lg",
        lados[lado],
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 opacity-60 hover:opacity-100">
        <X className="size-4" />
        <span className="sr-only">Cerrar</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
SheetContenido.displayName = "SheetContenido";
export {
  DialogEncabezado as SheetEncabezado,
  DialogTitulo as SheetTitulo,
  DialogDescripcion as SheetDescripcion,
  DialogPie as SheetPie,
};
